import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from './http-status-codes.component.module.scss';

interface StatusCode {
    code: number;
    name: string;
    category: '1xx' | '2xx' | '3xx' | '4xx' | '5xx';
    description: string;
    rfc?: string;
}

const STATUS_CODES: StatusCode[] = [
    { code: 100, name: 'Continue', category: '1xx', description: 'Initial part of the request received; client should continue.', rfc: 'RFC 9110 §15.2.1' },
    { code: 101, name: 'Switching Protocols', category: '1xx', description: 'Server agrees to switch protocols (e.g., to WebSocket).', rfc: 'RFC 9110 §15.2.2' },
    { code: 102, name: 'Processing', category: '1xx', description: 'WebDAV: server is processing but no response yet.', rfc: 'RFC 2518' },
    { code: 103, name: 'Early Hints', category: '1xx', description: 'Preload Link headers before final response.', rfc: 'RFC 8297' },

    { code: 200, name: 'OK', category: '2xx', description: 'Standard successful response.', rfc: 'RFC 9110 §15.3.1' },
    { code: 201, name: 'Created', category: '2xx', description: 'Request succeeded and a new resource was created.', rfc: 'RFC 9110 §15.3.2' },
    { code: 202, name: 'Accepted', category: '2xx', description: 'Request accepted but processing not complete.', rfc: 'RFC 9110 §15.3.3' },
    { code: 203, name: 'Non-Authoritative Information', category: '2xx', description: 'Returned data is from a transforming proxy.', rfc: 'RFC 9110 §15.3.4' },
    { code: 204, name: 'No Content', category: '2xx', description: 'Successful, but no body to return.', rfc: 'RFC 9110 §15.3.5' },
    { code: 205, name: 'Reset Content', category: '2xx', description: 'Client should reset the document view.', rfc: 'RFC 9110 §15.3.6' },
    { code: 206, name: 'Partial Content', category: '2xx', description: 'Range request fulfilled.', rfc: 'RFC 9110 §15.3.7' },
    { code: 207, name: 'Multi-Status', category: '2xx', description: 'WebDAV: multiple statuses in body.', rfc: 'RFC 4918' },
    { code: 208, name: 'Already Reported', category: '2xx', description: 'WebDAV: members already enumerated.', rfc: 'RFC 5842' },
    { code: 226, name: 'IM Used', category: '2xx', description: 'Server fulfilled GET with delta encoding.', rfc: 'RFC 3229' },

    { code: 300, name: 'Multiple Choices', category: '3xx', description: 'Multiple options for the resource.', rfc: 'RFC 9110 §15.4.1' },
    { code: 301, name: 'Moved Permanently', category: '3xx', description: 'Resource permanently moved to new URL.', rfc: 'RFC 9110 §15.4.2' },
    { code: 302, name: 'Found', category: '3xx', description: 'Resource temporarily at a different URL.', rfc: 'RFC 9110 §15.4.3' },
    { code: 303, name: 'See Other', category: '3xx', description: 'Use GET to retrieve the resource.', rfc: 'RFC 9110 §15.4.4' },
    { code: 304, name: 'Not Modified', category: '3xx', description: 'Cached version is still valid.', rfc: 'RFC 9110 §15.4.5' },
    { code: 307, name: 'Temporary Redirect', category: '3xx', description: 'Redirect; preserve method.', rfc: 'RFC 9110 §15.4.8' },
    { code: 308, name: 'Permanent Redirect', category: '3xx', description: 'Permanent redirect; preserve method.', rfc: 'RFC 9110 §15.4.9' },

    { code: 400, name: 'Bad Request', category: '4xx', description: 'Malformed request.', rfc: 'RFC 9110 §15.5.1' },
    { code: 401, name: 'Unauthorized', category: '4xx', description: 'Authentication required or failed.', rfc: 'RFC 9110 §15.5.2' },
    { code: 402, name: 'Payment Required', category: '4xx', description: 'Reserved for future use; sometimes used by APIs for quota.', rfc: 'RFC 9110 §15.5.3' },
    { code: 403, name: 'Forbidden', category: '4xx', description: 'Authenticated but not allowed.', rfc: 'RFC 9110 §15.5.4' },
    { code: 404, name: 'Not Found', category: '4xx', description: 'Resource does not exist.', rfc: 'RFC 9110 §15.5.5' },
    { code: 405, name: 'Method Not Allowed', category: '4xx', description: 'HTTP method not supported for resource.', rfc: 'RFC 9110 §15.5.6' },
    { code: 406, name: 'Not Acceptable', category: '4xx', description: 'Cannot produce content matching Accept header.', rfc: 'RFC 9110 §15.5.7' },
    { code: 407, name: 'Proxy Authentication Required', category: '4xx', description: 'Must authenticate with proxy.', rfc: 'RFC 9110 §15.5.8' },
    { code: 408, name: 'Request Timeout', category: '4xx', description: 'Server timed out waiting for the request.', rfc: 'RFC 9110 §15.5.9' },
    { code: 409, name: 'Conflict', category: '4xx', description: 'Request conflicts with current state.', rfc: 'RFC 9110 §15.5.10' },
    { code: 410, name: 'Gone', category: '4xx', description: 'Resource permanently removed.', rfc: 'RFC 9110 §15.5.11' },
    { code: 411, name: 'Length Required', category: '4xx', description: 'Content-Length header required.', rfc: 'RFC 9110 §15.5.12' },
    { code: 412, name: 'Precondition Failed', category: '4xx', description: 'If-Match / If-None-Match precondition failed.', rfc: 'RFC 9110 §15.5.13' },
    { code: 413, name: 'Content Too Large', category: '4xx', description: 'Request body too large.', rfc: 'RFC 9110 §15.5.14' },
    { code: 414, name: 'URI Too Long', category: '4xx', description: 'Request URL too long.', rfc: 'RFC 9110 §15.5.15' },
    { code: 415, name: 'Unsupported Media Type', category: '4xx', description: 'Content-Type not supported.', rfc: 'RFC 9110 §15.5.16' },
    { code: 416, name: 'Range Not Satisfiable', category: '4xx', description: 'Requested byte range not available.', rfc: 'RFC 9110 §15.5.17' },
    { code: 417, name: 'Expectation Failed', category: '4xx', description: 'Cannot meet Expect header requirements.', rfc: 'RFC 9110 §15.5.18' },
    { code: 418, name: "I'm a teapot", category: '4xx', description: 'April Fools joke from RFC 2324; sometimes used by APIs to refuse.', rfc: 'RFC 2324' },
    { code: 421, name: 'Misdirected Request', category: '4xx', description: 'Request directed at server unable to produce response.', rfc: 'RFC 9110 §15.5.20' },
    { code: 422, name: 'Unprocessable Content', category: '4xx', description: 'Semantic errors prevent processing.', rfc: 'RFC 9110 §15.5.21' },
    { code: 423, name: 'Locked', category: '4xx', description: 'WebDAV: resource is locked.', rfc: 'RFC 4918' },
    { code: 424, name: 'Failed Dependency', category: '4xx', description: 'WebDAV: failed due to a previous request failure.', rfc: 'RFC 4918' },
    { code: 425, name: 'Too Early', category: '4xx', description: 'Server unwilling to risk replay.', rfc: 'RFC 8470' },
    { code: 426, name: 'Upgrade Required', category: '4xx', description: 'Client must switch to a different protocol.', rfc: 'RFC 9110 §15.5.22' },
    { code: 428, name: 'Precondition Required', category: '4xx', description: 'Server requires conditional headers.', rfc: 'RFC 6585' },
    { code: 429, name: 'Too Many Requests', category: '4xx', description: 'Rate limit exceeded.', rfc: 'RFC 6585' },
    { code: 431, name: 'Request Header Fields Too Large', category: '4xx', description: 'Headers too large to process.', rfc: 'RFC 6585' },
    { code: 451, name: 'Unavailable For Legal Reasons', category: '4xx', description: 'Censored or legally restricted.', rfc: 'RFC 7725' },

    { code: 500, name: 'Internal Server Error', category: '5xx', description: 'Generic server-side error.', rfc: 'RFC 9110 §15.6.1' },
    { code: 501, name: 'Not Implemented', category: '5xx', description: 'Server does not support the request method.', rfc: 'RFC 9110 §15.6.2' },
    { code: 502, name: 'Bad Gateway', category: '5xx', description: 'Invalid response from upstream server.', rfc: 'RFC 9110 §15.6.3' },
    { code: 503, name: 'Service Unavailable', category: '5xx', description: 'Server temporarily overloaded or down for maintenance.', rfc: 'RFC 9110 §15.6.4' },
    { code: 504, name: 'Gateway Timeout', category: '5xx', description: 'Upstream server did not respond in time.', rfc: 'RFC 9110 §15.6.5' },
    { code: 505, name: 'HTTP Version Not Supported', category: '5xx', description: 'HTTP version is not supported.', rfc: 'RFC 9110 §15.6.6' },
    { code: 506, name: 'Variant Also Negotiates', category: '5xx', description: 'Internal config error in negotiation.', rfc: 'RFC 2295' },
    { code: 507, name: 'Insufficient Storage', category: '5xx', description: 'WebDAV: not enough storage.', rfc: 'RFC 4918' },
    { code: 508, name: 'Loop Detected', category: '5xx', description: 'WebDAV: infinite loop detected.', rfc: 'RFC 5842' },
    { code: 510, name: 'Not Extended', category: '5xx', description: 'Further extensions required.', rfc: 'RFC 2774' },
    { code: 511, name: 'Network Authentication Required', category: '5xx', description: 'Captive portal: must authenticate to use network.', rfc: 'RFC 6585' },
];

type Cat = 'all' | '1xx' | '2xx' | '3xx' | '4xx' | '5xx';

interface State {
    search: string;
    category: Cat;
}

export default class HttpStatusCodesComponent extends Component<{ feature?: string }, State> {
    state: State = { search: '', category: 'all' };

    private get filtered(): StatusCode[] {
        const q = this.state.search.trim().toLowerCase();
        return STATUS_CODES.filter(s => {
            if (this.state.category !== 'all' && s.category !== this.state.category) return false;
            if (!q) return true;
            return String(s.code).includes(q) || s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q);
        });
    }

    render() {
        const { search, category } = this.state;
        const rows = this.filtered;
        return (
            <div className="row">
                <div className="col-12">
                    <div className={styles.controls}>
                        <input className={styles.search} type="search" placeholder="Search code, name, description…"
                            value={search} onChange={e => this.setState({ search: e.target.value })}
                            aria-label="Filter status codes" />
                        <div className={styles.tabs} role="tablist">
                            {(['all', '1xx', '2xx', '3xx', '4xx', '5xx'] as Cat[]).map(c => (
                                <button key={c} role="tab"
                                    className={`${styles.tab} ${category === c ? styles.tabActive : ''}`}
                                    onClick={() => this.setState({ category: c })}
                                    aria-selected={category === c}>
                                    {c === 'all' ? 'All' : c}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <colgroup>
                                <col className={styles.colCode} />
                                <col className={styles.colName} />
                                <col className={styles.colDesc} />
                                <col className={styles.colRfc} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>Code</th>
                                    <th>Name</th>
                                    <th>Description</th>
                                    <th className={styles.rfcCol}>Reference</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(s => (
                                    <tr key={s.code} className={styles[`row${s.category}`]}>
                                        <td className={styles.codeCell}>{s.code}</td>
                                        <td className={styles.nameCell}>{s.name}</td>
                                        <td>{s.description}</td>
                                        <td className={styles.rfcCol}>{s.rfc ?? ''}</td>
                                    </tr>
                                ))}
                                {rows.length === 0 && (
                                    <tr><td colSpan={4} className={styles.empty}>No status codes match your filter.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <p className={sharedStyles.label} style={{ color: '#64748b', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                        Showing {rows.length} of {STATUS_CODES.length} codes.
                    </p>
                </div>
            </div>
        );
    }
}
